import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import useColdefManageInformation from '../../../hooks/grid/coldef/useColdefManageInformation';
import commonColDef from '../../../data/grid/commonColDef';
import ActionsManageInformation from '../../cellRenderers/ActionsManageInformation';
import useGridOptionsManageInformation from '../../../hooks/grid/gridOptions/useGridOptionsManageInformation';
import BooleanRenderer from '../../cellRenderers/BooleanRenderer';
import UserNameFromEmailCellRenderer from '../../cellRenderers/UserNameFromEmailCellRenderer';
import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { setDialogMode, openDialog, selectInformation } from '../../../redux/slices/information';
import type { ColDef, ColumnApi, GridApi, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import type { InformationType } from '../../../types/information';

const GridInformation = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isLoading, informations } = useSelector((state: RootState) => state.information);
  const { users } = useSelector((state: RootState) => state.user);
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsManageInformation();
  const customColDef: ColDef = {
    cellClass: 'cell',
    flex: 1,
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const colDef: ColDef[] = useColdefManageInformation(t);
  const frameworkComponents = {
    actionsRenderer: ActionsManageInformation,
    booleanRenderer: BooleanRenderer,
    userNameFromEmailCellRenderer: UserNameFromEmailCellRenderer,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (informations?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, informations]);

  useEffect(() => {
    if (gridApi && gridColumnApi) {
      const columnKeys: string[] = ['updatedAt', 'updatedBy', 'createdAt', 'createdBy'];
      gridColumnApi.autoSizeColumns(columnKeys);
      gridApi.redrawRows();
    }
  }, [gridColumnApi, informations]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * ダブルクリックで編集モードにする
   * @param params GridReadyEvent
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    const data: InformationType = event.data;
    // console.log(data);
    dispatch(selectInformation(data));
    dispatch(setDialogMode('UPDATE'));
    dispatch(openDialog());
  };

  // VIEW
  // ================================================================================
  return (
    <div className="ag-theme-balham" style={{ height: '70vh' }}>
      <AgGridReact
        immutableData={true}
        gridOptions={gridOptions}
        defaultColDef={defaultColDef}
        columnDefs={colDef}
        rowData={informations}
        onGridReady={onGridReady}
        onRowDoubleClicked={onRowDoubleClicked}
        frameworkComponents={frameworkComponents}
        // filterValueGetterで使うデータ
        context={{
          // reduxにあるものはコピーを渡す
          users: users?.map((user) => structuredClone(user)),
        }}
      />
    </div>
  );
};

export default GridInformation;
